import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  supabase,
  isSupabaseConfigured,
  selectData,
  insertData,
} from "../lib/supabase";

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [billingSettings, setBillingSettings] = useState({
    company_name: "",
    company_address: "",
    company_email: "",
    company_phone: "",
    beneficiary_name: "",
    iban: "",
    bic: "",
    bank_name: "",
    payment_terms: "Paiement à réception",
    payment_note: "Merci d’indiquer le numéro de facture en référence.",
  });

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      setMessage("");

      if (!isSupabaseConfigured || !supabase) {
        setMessage("Supabase n'est pas configuré.");
        return;
      }

      const result = await selectData("billing_settings", {
        order: { column: "updated_at", ascending: false },
        limit: 1,
      });

      if (result.error) throw result.error;

      if (result.data?.length > 0) {
        const s = result.data[0];
        setBillingSettings({
          company_name: s.company_name || "",
          company_address: s.company_address || "",
          company_email: s.company_email || "",
          company_phone: s.company_phone || "",
          beneficiary_name: s.beneficiary_name || "",
          iban: s.iban || "",
          bic: s.bic || "",
          bank_name: s.bank_name || "",
          payment_terms: s.payment_terms || "Paiement à réception",
          payment_note:
            s.payment_note ||
            "Merci d’indiquer le numéro de facture en référence.",
        });
      }
    } catch (error) {
      console.error(error);
      setMessage("Impossible de charger les paramètres.");
    } finally {
      setLoading(false);
    }
  }

  function updateField(field, value) {
    setBillingSettings((prev) => ({ ...prev, [field]: value }));
  }

  async function saveSettings() {
    try {
      setSaving(true);
      setMessage("");

      if (!isSupabaseConfigured || !supabase) {
        setMessage("Mode démo : impossible d’enregistrer.");
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      const payload = {
        ...billingSettings,
        updated_by: session?.user?.id || null,
      };

      const result = await insertData("billing_settings", payload);

      if (result.error) throw result.error;

      setMessage("Paramètres enregistrés avec succès.");
      await loadSettings();
    } catch (error) {
      console.error(error);
      setMessage("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topbar}>
          <div>
            <h1 style={styles.title}>Paramètres d’entreprise</h1>
            <p style={styles.subtitle}>
              Gère ici les informations de facturation et les coordonnées bancaires affichées sur les factures.
            </p>
          </div>

          <Link to="/admin" style={styles.secondaryButton}>
            Retour au dashboard
          </Link>
        </div>

        {message ? (
          <div
            style={
              message.toLowerCase().includes("succès")
                ? styles.alertSuccess
                : styles.alertError
            }
          >
            {message}
          </div>
        ) : null}

        <div style={styles.card}>
          {loading ? (
            <p>Chargement...</p>
          ) : (
            <>
              <div style={styles.field}>
                <label>Nom de l’entreprise</label>
                <input
                  style={styles.input}
                  value={billingSettings.company_name}
                  onChange={(e) => updateField("company_name", e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label>Adresse</label>
                <textarea
                  rows={3}
                  style={styles.textarea}
                  value={billingSettings.company_address}
                  onChange={(e) => updateField("company_address", e.target.value)}
                />
              </div>

              <div style={styles.twoCols}>
                <div style={styles.field}>
                  <label>Email</label>
                  <input
                    style={styles.input}
                    value={billingSettings.company_email}
                    onChange={(e) => updateField("company_email", e.target.value)}
                  />
                </div>

                <div style={styles.field}>
                  <label>Téléphone</label>
                  <input
                    style={styles.input}
                    value={billingSettings.company_phone}
                    onChange={(e) => updateField("company_phone", e.target.value)}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label>Bénéficiaire</label>
                <input
                  style={styles.input}
                  value={billingSettings.beneficiary_name}
                  onChange={(e) => updateField("beneficiary_name", e.target.value)}
                />
              </div>

              <div style={styles.twoCols}>
                <div style={styles.field}>
                  <label>IBAN</label>
                  <input
                    style={styles.input}
                    value={billingSettings.iban}
                    onChange={(e) => updateField("iban", e.target.value)}
                  />
                </div>

                <div style={styles.field}>
                  <label>BIC</label>
                  <input
                    style={styles.input}
                    value={billingSettings.bic}
                    onChange={(e) => updateField("bic", e.target.value)}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label>Banque</label>
                <input
                  style={styles.input}
                  value={billingSettings.bank_name}
                  onChange={(e) => updateField("bank_name", e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label>Conditions de paiement</label>
                <input
                  style={styles.input}
                  value={billingSettings.payment_terms}
                  onChange={(e) => updateField("payment_terms", e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label>Note de paiement</label>
                <textarea
                  rows={3}
                  style={styles.textarea}
                  value={billingSettings.payment_note}
                  onChange={(e) => updateField("payment_note", e.target.value)}
                />
              </div>

              <button
                style={styles.primaryButton}
                onClick={saveSettings}
                disabled={saving}
              >
                {saving ? "Enregistrement..." : "Enregistrer les paramètres"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#0b1020",
    minHeight: "100vh",
    padding: "40px 20px",
    color: "#fff",
  },
  container: {
    maxWidth: "1000px",
    margin: "0 auto",
  },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: "8px",
  },
  subtitle: {
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.6,
    maxWidth: "760px",
  },
  card: {
    background: "#121933",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px",
    padding: "24px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "14px",
  },
  input: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "#fff",
    outline: "none",
  },
  textarea: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "#fff",
    outline: "none",
    resize: "vertical",
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  primaryButton: {
    padding: "12px 18px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#4f7cff",
    color: "#fff",
    fontWeight: 700,
    marginTop: "12px",
  },
  secondaryButton: {
    padding: "12px 16px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "#1a2347",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 600,
  },
  alertError: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(255, 87, 87, 0.12)",
    border: "1px solid rgba(255, 87, 87, 0.28)",
    marginBottom: "18px",
  },
  alertSuccess: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(80, 200, 120, 0.12)",
    border: "1px solid rgba(80, 200, 120, 0.28)",
    marginBottom: "18px",
  },
};